import { toast } from 'sonner'

import type { useCorrectShipmentStatus } from '@/api/hooks'
import { ManualStatusControl } from '@/components/manual-status-control'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { SHIPMENT_STATUS_CHOICES, shipmentStatusVariant } from '@/components/manual-status-options'
import { Badge } from '@/components/ui/badge'
import { formatApiErrorMessage } from '@/lib/api-error'
import { cn, formatDateOnly, formatStatus } from '@/lib/utils'

const TRACKING_EDIT_REASON = 'Tracking details changed on the dashboard'

interface EditableShipment {
  id: number
  status: string
  status_revision: number
  status_source: string
  estimated_ship_date?: string | null
  carrier_name?: string | null
  tracking_numbers?: string | null
  pro_number?: string | null
  bol_number?: string | null
  pickup_number?: string | null
  bol_reference?: string | null
}

/** The shipment's status pill; clicking it picks a new status and saves it. */
export function ShipmentStatusEdit({
  shipment,
  correct,
}: {
  shipment: EditableShipment
  correct: ReturnType<typeof useCorrectShipmentStatus>
}) {
  return (
    <ManualStatusControl
      entityLabel="shipment"
      currentStatus={shipment.status}
      statusRevision={shipment.status_revision}
      statusSource={shipment.status_source}
      statuses={SHIPMENT_STATUS_CHOICES}
      loading={correct.isPending && correct.variables?.shipmentId === shipment.id}
      onSubmit={(body, callbacks) => correct.mutate({ shipmentId: shipment.id, body }, callbacks)}
      trigger={<Badge variant={shipmentStatusVariant(shipment.status)}>{formatStatus(shipment.status)}</Badge>}
    />
  )
}

type ReferenceField =
  | 'carrier_name'
  | 'tracking_numbers'
  | 'pro_number'
  | 'bol_number'
  | 'pickup_number'
  | 'bol_reference'
  | 'estimated_ship_date'

const REFERENCE_FIELDS: Array<{ key: ReferenceField; label: string; kind?: 'date'; mono?: boolean }> = [
  { key: 'carrier_name', label: 'Carrier' },
  { key: 'tracking_numbers', label: 'Tracking', mono: true },
  { key: 'pro_number', label: 'PRO', mono: true },
  { key: 'bol_number', label: 'BOL', mono: true },
  { key: 'pickup_number', label: 'Pickup #', mono: true },
  { key: 'bol_reference', label: "Supplier's BOL ref", mono: true },
  { key: 'estimated_ship_date', label: 'Est. ship', kind: 'date' },
]

/** Stored tracking is a JSON list or comma text; staff read and type it as comma text. */
function trackingText(raw: string | null | undefined) {
  if (!raw) return ''
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(String).join(', ')
  } catch {
    // Plain comma text.
  }
  return raw
}

/**
 * Carrier, tracking, the reference numbers and ESD, each click to edit and saved on its own
 * (USA-1466). Works in every status, delivered included; the status stays as it is.
 */
export function ShipmentReferenceFields({
  shipment,
  correct,
  cellClassName,
}: {
  shipment: EditableShipment
  correct: ReturnType<typeof useCorrectShipmentStatus>
  cellClassName?: string
}) {
  const save = (key: ReferenceField, raw: string) =>
    correct.mutate(
      {
        shipmentId: shipment.id,
        body: {
          status: shipment.status,
          reason: TRACKING_EDIT_REASON,
          expected_status: shipment.status,
          expected_revision: shipment.status_revision,
          allow_regression: false,
          [key]: raw.trim() || null,
        },
      },
      { onError: (error) => toast.error(formatApiErrorMessage('Shipment not saved', error)) },
    )
  const pending = correct.isPending && correct.variables?.shipmentId === shipment.id

  return (
    <>
      {REFERENCE_FIELDS.map(({ key, label, kind, mono }) => {
        const value = key === 'tracking_numbers' ? trackingText(shipment[key]) : (shipment[key] ?? '')
        return (
          <div key={key} className={cn('min-w-0', cellClassName)}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <POInlineEditableField
              value={value}
              displayValue={kind === 'date' && value ? formatDateOnly(value) : undefined}
              placeholder="Add"
              kind={kind ?? 'text'}
              pending={pending}
              ariaLabel={`${label}: ${value || 'not set'}. Edit`}
              showEditIcon
              buttonClassName={cn('-ml-2 min-w-16', mono && 'font-mono', !value && 'text-muted-foreground')}
              inputClassName="w-full min-w-40"
              onConfirm={(raw) => save(key, raw)}
            />
          </div>
        )
      })}
    </>
  )
}
